import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import {
  adminAccountPath,
  adminAccountsAllBadgeRecomputationPath,
  adminAccountsPath,
  newAdminAccountPath,
} from "@travily/routes";
import type AdminAccountsIndex from "@travily/types/pages/AdminAccountsIndex";
import { Award02, Plus } from "@untitledui/icons";
import { type ReactNode, useCallback } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminListNavigation } from "@/hooks/admin/use-admin-list-navigation";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { type AccountFilters, IndexView } from "./index.view";

type AdminAccountsIndexPageProps = AdminAccountsIndex;

const FILTER_TO_RANSACK: Record<keyof AccountFilters, string> = {
  search: "profile_name_or_handle_cont",
  profile_type: "profile_profileable_type_eq",
  visibility: "profile_visibility_eq",
  setup_status: "setup_status_eq",
  claimed: "claimed_eq",
  include_personal: "include_personal",
};

function AdminAccountsIndexPage({ accounts, pagy, sort, sort_dir, filters }: AdminAccountsIndexPageProps) {
  const account = useAdminLayoutAccount();
  const { visitModal } = useModalStack();

  const { handleSortChange, handlePageChange, handleSearchChange, handleFilterChange } =
    useAdminListNavigation<AccountFilters>({
      basePath: adminAccountsPath(),
      filters,
      sort,
      sortDir: sort_dir,
      filterToRansack: FILTER_TO_RANSACK,
    });

  const handleRecomputeBadges = useCallback(
    () => router.post(adminAccountsAllBadgeRecomputationPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleDeleteAccount = useCallback(
    (accountId: number, force: boolean) =>
      router.delete(adminAccountPath(accountId), { data: { force }, preserveScroll: true }),
    [],
  );

  const accountCount = formatCount(pagy.count, "account");

  return (
    <AdminLayout activeUrl={adminAccountsPath()} account={account}>
      <Head title="Accounts - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Accounts"
        titleBadge={
          <Badge color="gray" size="sm">
            {accountCount}
          </Badge>
        }
        subtitle="All registered accounts"
        actions={
          <>
            <Button size="sm" color="primary" iconLeading={Plus} onClick={() => visitModal(newAdminAccountPath())}>
              New account
            </Button>
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "recompute_badges") handleRecomputeBadges();
                  }}
                >
                  <Dropdown.Item id="recompute_badges" icon={Award02} label="Recompute badges" />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          accounts={accounts}
          pagy={pagy}
          sort={sort}
          sortDir={sort_dir}
          filters={filters}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
          onDeleteAccount={handleDeleteAccount}
        />
      </div>
    </AdminLayout>
  );
}

AdminAccountsIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminAccountsIndexPage;
